import { useState } from "react";
import { Check, X, RotateCcw } from "lucide-react";
import confetti from "canvas-confetti";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";

export function ChapterQuiz({ book, ch }: { book: number; ch: number }) {
  const [quiz, setQuiz] = useState<any>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [i, setI] = useState(0);
  const [pick, setPick] = useState<number | null>(null);
  const [score, setScore] = useState(0);
  const start = async () => {
    setBusy(true); setErr(""); setQuiz(null); setI(0); setPick(null); setScore(0);
    try { setQuiz((await api.post("/deep/quiz", { book, ch })).data); } catch (e) { setErr(errMsg(e)); } finally { setBusy(false); }
  };
  if (!quiz) return (
    <div className="space-y-4" data-testid="quiz-start">
      <p className="text-sm text-slate-600">Five quick questions on this chapter, each pointing back to the verse it comes from.</p>
      {err && <p className="text-sm text-red-700" data-testid="quiz-error">{err}</p>}
      <Button data-testid="quiz-start-button" disabled={busy} onClick={start} className="rounded-full bg-[#1E293B]">{busy ? "Writing your quiz…" : "Quiz me on this chapter"}</Button>
    </div>
  );
  const qs = quiz.questions;
  if (i >= qs.length) return (
    <div className="space-y-4 text-center" data-testid="quiz-result">
      <p className="font-display text-5xl font-semibold text-slate-900">{score}/{qs.length}</p>
      <p className="text-slate-600">{score === qs.length ? "Every one right. Well read!" : "Good going. Read it once more and try again."}</p>
      <Button data-testid="quiz-again" onClick={start} variant="outline" className="rounded-full"><RotateCcw className="mr-2 h-4 w-4" />New quiz</Button>
    </div>
  );
  const q = qs[i];
  const choose = (k: number) => {
    if (pick !== null) return;
    setPick(k);
    if (k === q.answer) { setScore((s) => s + 1); if (i === qs.length - 1 && score + 1 === qs.length) confetti({ particleCount: 60, spread: 60, origin: { y: 0.7 }, disableForReducedMotion: true }); }
  };
  return (
    <div className="space-y-4" data-testid={`quiz-q-${i}`}>
      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Question {i + 1} of {qs.length}</p>
      <p className="font-display text-2xl font-semibold leading-snug text-slate-900">{q.q}</p>
      <div className="space-y-2">
        {q.options.map((o: string, k: number) => {
          const state = pick === null ? "" : k === q.answer ? "border-emerald-500 bg-emerald-50" : k === pick ? "border-red-400 bg-red-50" : "opacity-60";
          return (
            <button key={k} data-testid={`quiz-option-${k}`} onClick={() => choose(k)} className={`flex w-full items-center justify-between rounded-xl border border-[#E2DBCE] bg-white px-4 py-3 text-left text-sm font-medium text-slate-800 transition-colors hover:bg-[#FBF7EF] ${state}`}>
              {o}{pick !== null && k === q.answer && <Check className="h-4 w-4 text-emerald-600" />}{pick === k && k !== q.answer && <X className="h-4 w-4 text-red-500" />}
            </button>
          );
        })}
      </div>
      {pick !== null && (
        <div className="space-y-3" data-testid="quiz-feedback">
          <p className="rounded-xl bg-amber-50 p-3 text-sm text-amber-950">{q.why} <span className="font-semibold">{String(q.ref || "").replace(/[()]/g, "")}</span></p>
          <Button data-testid="quiz-next" onClick={() => { setI(i + 1); setPick(null); }} className="rounded-full bg-[#C86D51] hover:bg-[#B35A3F]">{i === qs.length - 1 ? "See my score" : "Next question"}</Button>
        </div>
      )}
    </div>
  );
}
